// Resident Polling Page - View active polls and vote
import React, { useState, useMemo } from "react";
import {
  CheckCircle2, 
  Circle, 
  Clock, 
  BarChart3, 
  Info,
  AlertCircle,
  TrendingUp
} from "lucide-react";
import { useActivePolls, usePoll, useVotePoll, usePollResults } from "@/api/polls";
import { useToast } from "@/hooks/use-toast";
import type { Poll, PollListItem, PollOption } from "@/types/poll";
import { useLanguage } from "@/contexts/LanguageContext";

const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50";

const PRIMARY = "#1F3A93";

export default function ResidentPollingPage() {
  const [selectedPollId, setSelectedPollId] = useState<string | null>(null);
  const [viewingResultsId, setViewingResultsId] = useState<string | null>(null);
  const { t } = useLanguage();

  const { data: polls, isLoading } = useActivePolls();
  const { toast } = useToast();

  // Sort polls: active first, then by start date
  const sortedPolls = useMemo(() => {
    if (!polls) return [];
    return [...polls].sort((a, b) => {
      if (a.is_active_now !== b.is_active_now) {
        return a.is_active_now ? -1 : 1;
      }
      return +new Date(b.starts_at) - +new Date(a.starts_at);
    });
  }, [polls]);

  const hasVotedPolls = useMemo(() => {
    if (!polls) return new Set<string>();
    return new Set(polls.filter((p) => p.has_voted).map((p) => p.id));
  }, [polls]);

  return (
    <div className="relative space-y-6 sm:space-y-7">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Header */}
      <div className="rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t("polls.communityTitle")}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {isLoading
                ? t("common.loading")
                : t("polls.activeCount", { count: sortedPolls.length })}
            </p>
          </div>
          <BarChart3 className="h-8 w-8 text-gray-400" />
        </div>
      </div>

      {/* Info Card */}
      <div className="rounded-2xl bg-blue-50/80 backdrop-blur ring-1 ring-blue-200/50 p-4 sm:p-5">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">{t("polls.howToVoteTitle")}</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>{t("polls.howToVote.step1")}</li>
              <li>{t("polls.howToVote.step2")}</li>
              <li>{t("polls.howToVote.step3")}</li>
              <li>{t("polls.howToVote.step4")}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Polls List */}
      {isLoading ? (
        <div className="rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-gray-600">{t("polls.loading")}</p>
        </div>
      ) : sortedPolls.length === 0 ? (
        <div className="rounded-2xl bg-white/80 backdrop-blur ring-1 ring-black/5 p-8 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-4 text-sm text-gray-600">{t("polls.noneActive")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2">
          {sortedPolls.map((poll) => (
            <PollCard
              key={poll.id}
              poll={poll}
              hasVoted={hasVotedPolls.has(poll.id)}
              onVote={() => setSelectedPollId(poll.id)}
              onViewResults={() => setViewingResultsId(poll.id)}
            />
          ))}
        </div>
      )}

      {/* Vote Modal */}
      {selectedPollId && (
        <VoteModal
          pollId={selectedPollId}
          onClose={() => setSelectedPollId(null)}
          onSuccess={() => {
            setSelectedPollId(null);
            toast({
              title: t("polls.voteRecorded"),
              description: t("polls.voteThanks"),
            });
          }}
        />
      )}

      {/* Results Modal */}
      {viewingResultsId && (
        <ResultsModal
          pollId={viewingResultsId}
          onClose={() => setViewingResultsId(null)}
        />
      )}
    </div>
  );
}

/** ----------------------------------------------------------------
 * Poll Card Component
 * ----------------------------------------------------------------*/
interface PollCardProps {
  poll: PollListItem;
  hasVoted: boolean;
  onVote: () => void;
  onViewResults: () => void;
}

function PollCard({ poll, hasVoted, onVote, onViewResults }: PollCardProps) {
  const isActive = poll.is_active_now;
  const endsAt = poll.ends_at ? new Date(poll.ends_at) : null;
  const now = new Date();
  const isClosingSoon = endsAt && endsAt.getTime() - now.getTime() < 24 * 60 * 60 * 1000; // 24 hours
  const { t } = useLanguage();

  return (
    <div className="rounded-2xl bg-white/90 backdrop-blur ring-1 ring-black/5 p-5 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold text-gray-900 flex-1">
          {poll.question}
        </h3>
        {hasVoted ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800 flex-shrink-0">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("polls.status.voted")}
          </span>
        ) : isClosingSoon ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800 flex-shrink-0">
            <Clock className="h-3.5 w-3.5" />
            {t("polls.status.closingSoon")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800 flex-shrink-0">
            <Circle className="h-3.5 w-3.5" />
            {t("polls.status.active")}
          </span>
        )}
      </div>

      {/* Description */}
      {poll.description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{poll.description}</p>
      )}

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500 mb-4">
        <div className="flex items-center gap-1">
          <TrendingUp className="h-4 w-4" />
          <span>{t("polls.voteCount", { count: poll.total_votes })}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="h-4 w-4" />
          {endsAt ? (
            <span>{t("polls.ends")} {formatDate(poll.ends_at!, t)}</span>
          ) : (
            <span>{t("polls.noEndDate")}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {!hasVoted && isActive ? (
          <button
            onClick={onVote}
            className="flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px] transition-all"
            style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, #27AE60 100%)` }}
          >
            {t("polls.voteNow")}
          </button>
        ) : (
          <button
            onClick={onViewResults}
            className={`flex-1 px-4 py-2.5 text-sm font-semibold ${QUIET}`}
          >
            {t("polls.viewResults")}
          </button>
        )}
      </div>
    </div>
  );
}

/** ----------------------------------------------------------------
 * Vote Modal Component
 * ----------------------------------------------------------------*/
interface VoteModalProps {
  pollId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function VoteModal({ pollId, onClose, onSuccess }: VoteModalProps) {
  const { data: poll, isLoading } = usePoll(pollId);
  const voteMutation = useVotePoll();
  const { toast } = useToast();
  const { t } = useLanguage();

  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());

  const handleOptionToggle = (optionId: string) => {
    if (!poll) return;

    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (poll.allow_multiple_votes) {
        // Toggle for multiple choice
        if (next.has(optionId)) {
          next.delete(optionId);
        } else {
          next.add(optionId);
        }
      } else {
        // Single choice only
        next.clear();
        next.add(optionId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedOptions.size === 0) {
      toast({
        title: t("polls.noOptionTitle"),
        description: t("polls.noOptionDesc"),
      });
      return;
    }

    try {
      // For now, backend only supports single vote
      const optionId = Array.from(selectedOptions)[0];
      await voteMutation.mutateAsync({ id: pollId, data: { option_id: optionId } });
      onSuccess();
    } catch (error: any) {
      toast({
        title: t("polls.voteFailed"),
        description: error.response?.data?.detail || error.message || t("polls.submitFailed"),
      });
    }
  };

  if (isLoading || !poll) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg">
          <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
              <p className="mt-4 text-sm text-gray-600">{t("polls.loadingPoll")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has already voted (from poll data)
  if (poll.user_vote) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg">
          <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t("polls.alreadyVotedTitle")}</h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                {t("common.close")}
              </button>
            </div>
            <div className="rounded-lg bg-green-50 p-4 mb-4">
              <div className="flex gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">{t("polls.alreadyVotedDesc")}</p>
                  <p>{t("polls.alreadyVotedNote")}</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`w-full px-4 py-2.5 text-sm font-semibold ${QUIET}`}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg">
        <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t("polls.castVoteTitle")}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              {t("common.close")}
            </button>
          </div>

          {/* Poll Question */}
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-2">{poll.question}</h4>
            {poll.description && (
              <p className="text-sm text-gray-600">{poll.description}</p>
            )}
          </div>

          {/* Info Banner */}
          <div className="mb-6 rounded-lg bg-blue-50 p-3">
            <div className="flex gap-2 text-xs text-blue-800">
              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <p>
                {poll.allow_multiple_votes
                  ? t("polls.multipleOptions")
                  : t("polls.singleOption")}
              </p>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3 mb-6">
            {poll.options.map((option) => {
              const isSelected = selectedOptions.has(option.id);
              return (
                <button
                  key={option.id}
                  onClick={() => handleOptionToggle(option.id)}
                  className={`w-full text-left rounded-xl border-2 p-4 transition-all ${
                    isSelected
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {isSelected && <CheckCircle2 className="h-4 w-4 text-white" />}
                    </div>
                    <span className="text-sm font-medium text-gray-900">{option.text}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold ${QUIET}`}
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedOptions.size === 0 || voteMutation.isPending}
              className="flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-black/10 hover:shadow-xl active:translate-y-[1px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY} 70%, #27AE60 100%)` }}
            >
              {voteMutation.isPending ? t("polls.submitting") : t("polls.submitVote")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ----------------------------------------------------------------
 * Results Modal Component
 * ----------------------------------------------------------------*/
interface ResultsModalProps {
  pollId: string;
  onClose: () => void;
}

function ResultsModal({ pollId, onClose }: ResultsModalProps) {
  const { data: results, isLoading } = usePollResults(pollId);
  const { t } = useLanguage();

  if (isLoading || !results) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg">
          <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl">
            <div className="text-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
              <p className="mt-4 text-sm text-gray-600">{t("polls.loadingResults")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalVotes = results.total_votes;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg">
        <div className="m-0 rounded-t-3xl sm:rounded-3xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t("polls.resultsTitle")}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              {t("common.close")}
            </button>
          </div>

          {/* Poll Question */}
          <div className="mb-6">
            <h4 className="text-base font-medium text-gray-900 mb-2">{results.question}</h4>
            {results.description && (
              <p className="text-sm text-gray-600">{results.description}</p>
            )}
          </div>

          {/* Status Badge */}
          <div className="mb-6 flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                results.status === "active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {results.status === "active" ? t("polls.status.active") : t("polls.status.closed")}
            </span>
            <span className="text-xs text-gray-500">
              {t("polls.voteSummary", { total: totalVotes, voters: results.unique_voters })}
            </span>
          </div>

          {/* Results */}
          <div className="space-y-4 mb-6">
            {results.options
              .sort((a, b) => b.vote_count - a.vote_count)
              .map((option) => {
                const percentage = option.vote_percentage;
                return (
                  <div key={option.id}>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-medium text-gray-900">{option.text}</span>
                      <span className="text-gray-600">
                        {option.vote_count} ({percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${PRIMARY} 0%, #27AE60 100%)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className={`w-full px-4 py-2.5 text-sm font-semibold ${QUIET}`}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** ----------------------------------------------------------------
 * Helper Functions
 * ----------------------------------------------------------------*/
function formatDate(dateString: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) {
    return t("polls.ended");
  } else if (diffHours < 24) {
    return t("polls.inHours", { count: diffHours });
  } else if (diffDays < 7) {
    return t("polls.inDays", { count: diffDays });
  } else {
    return date.toLocaleDateString();
  }
}
